import { createSlice } from '@reduxjs/toolkit';
// ─── Temp-ID counter ─────────────────────────────────────────────────────────
// New (unsaved) questions and options receive a negative integer as their
// client-only temp ID.  The value carries meaning only within this session;
// it is never sent to the server as-is (see buildBulkSavePayload in queries).
// Resets on page reload — drafts are not persisted across refreshes.
// Counting starts well below -1 because the rearrange tree uses -1 as its
// "root level" sentinel for branchingFrom (see rearrange/utils.ts). Starting at
// -1 here would let the first new option in a session be mistaken for the root.
const CLIENT_ID_BASE = -1000;
let clientIdCounter = CLIENT_ID_BASE;
/** Returns the next unique negative client ID for a new question or option. */ export const nextClientId = ()=>--clientIdCounter;
/** Returns true when an id is a draft-only temp ID (not yet saved). */ export const isClientId = (id)=>id < 0;
/**
 * Converts a negative client ID to the clientId string sent in the bulk-save
 * request body and returned in idMappings.
 */ export const clientIdKey = (type, id)=>`tmp_${type}_${Math.abs(id)}`;
// ─── Tree-traversal helpers ───────────────────────────────────────────────────
// All helpers are written to be Immer-safe: they either mutate the draft
// proxy in-place (via a callback) or return plain new values that can be
// assigned back into the draft.
/** Find a question anywhere in the tree and call fn on it. Returns true if found. */ function applyToQuestion(questions, questionId, fn) {
    for (const q of questions){
        if (q.questionId === questionId) {
            fn(q);
            return true;
        }
        if (q.options) {
            for (const opt of q.options){
                if (opt.branches && applyToQuestion(opt.branches, questionId, fn)) {
                    return true;
                }
            }
        }
        if (q.rows && applyToQuestion(q.rows, questionId, fn)) {
            return true;
        }
    }
    return false;
}
/** Find an option anywhere in the tree and call fn on it. Returns true if found. */ function applyToOption(questions, optionId, fn) {
    for (const q of questions){
        var _q_options;
        const found = ((_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []).find((o)=>o.optionId === optionId);
        if (found) {
            fn(found);
            return true;
        }
        if (q.options) {
            for (const opt of q.options){
                if (opt.branches && applyToOption(opt.branches, optionId, fn)) {
                    return true;
                }
            }
        }
        if (q.rows && applyToOption(q.rows, optionId, fn)) {
            return true;
        }
    }
    return false;
}
/** Find the question with the given id anywhere in the tree. */ export function findQuestion(questions, questionId) {
    for (const q of questions){
        var _q_options;
        if (q.questionId === questionId) return q;
        for (const opt of (_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []){
            const hit = opt.branches && findQuestion(opt.branches, questionId);
            if (hit) return hit;
        }
        const rowHit = q.rows && findQuestion(q.rows, questionId);
        if (rowHit) return rowHit;
    }
    return undefined;
}
/** Collect every questionId in a subtree (the question itself plus branches and rows). */ function collectQuestionIds(questions, acc) {
    for (const q of questions){
        var _q_options;
        acc.push(q.questionId);
        for (const opt of (_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []){
            if (opt.branches) collectQuestionIds(opt.branches, acc);
        }
        if (q.rows) collectQuestionIds(q.rows, acc);
    }
    return acc;
}
/**
 * Remove a question from the tree, cascading into option.branches and rows.
 * Calls onLevelRepacked with the level before and after removal so the caller can
 * tell which siblings actually shifted position.
 */ function removeQuestionFromTree(questions, questionId, onLevelRepacked) {
    if (questions.some((q)=>q.questionId === questionId)) {
        const repacked = repackPositions(questions.filter((q)=>q.questionId !== questionId));
        onLevelRepacked(questions, repacked);
        return repacked;
    }
    return questions.map((q)=>{
        const next = {
            ...q
        };
        if (q.options) {
            next.options = q.options.map((opt)=>({
                    ...opt,
                    branches: opt.branches ? removeQuestionFromTree(opt.branches, questionId, onLevelRepacked) : opt.branches
                }));
        }
        if (q.rows) {
            next.rows = removeQuestionFromTree(q.rows, questionId, onLevelRepacked);
        }
        return next;
    });
}
/**
 * Reorder `current` to match the order named by `incoming`, keeping the entities
 * that already live in the draft.
 *
 * The reorder callers read their list out of the antd form, which was seeded
 * from the panel's selection snapshot. That snapshot goes stale as soon as the
 * canvas edits the same question — deleting a branch off an option, for
 * instance — so handing the array straight back would resurrect whatever the
 * snapshot still remembers. Only the ordering is taken from the caller; the
 * objects themselves come from the draft. Entities the snapshot never saw keep
 * their relative order at the end rather than being dropped.
 */ function reorderById(current, incoming, idOf) {
    const byId = new Map(current.map((entity)=>[
            idOf(entity),
            entity
        ]));
    const ordered = [];
    incoming.forEach((entity)=>{
        const id = idOf(entity);
        const existing = byId.get(id);
        if (existing !== undefined) {
            ordered.push(existing);
            byId.delete(id);
        }
    });
    // Whatever the caller did not name is appended in its existing order.
    byId.forEach((entity)=>ordered.push(entity));
    return ordered;
}
/** Re-number question positions as 1-based sequential integers. */ function repackPositions(questions) {
    return questions.map((q, idx)=>({
            ...q,
            position: idx + 1
        }));
}
/** Insert newQuestion into questions so it ends up at insertAtPosition (1-based). */ function addQuestionAtPosition(questions, newQuestion, insertAtPosition) {
    if (insertAtPosition === undefined) {
        return repackPositions([
            ...questions,
            newQuestion
        ]);
    }
    const insertIdx = questions.findIndex((q)=>q.position >= insertAtPosition);
    const next = [
        ...questions
    ];
    if (insertIdx === -1) {
        next.push(newQuestion);
    } else {
        next.splice(insertIdx, 0, newQuestion);
    }
    return repackPositions(next);
}
/**
 * The array a branch level lives in: the root question list when branchingFrom is
 * null/undefined, otherwise that option's `branches`.  Returns the live array so
 * callers can splice it in place (Immer tracks the mutation).
 */ function getBranchLevel(roots, branchingFrom) {
    if (branchingFrom === null || branchingFrom === undefined) {
        return roots;
    }
    let level = null;
    applyToOption(roots, branchingFrom, (opt)=>{
        if (!opt.branches) opt.branches = [];
        level = opt.branches;
    });
    return level;
}
/** Every option ID inside a subtree, including the roots' own options. */ function collectOptionIds(questions, acc) {
    for (const q of questions){
        var _q_options;
        for (const opt of (_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []){
            acc.push(opt.optionId);
            if (opt.branches) collectOptionIds(opt.branches, acc);
        }
        if (q.rows) collectOptionIds(q.rows, acc);
    }
    return acc;
}
/**
 * Remove a question from wherever it sits, in place, and return it with its
 * subtree intact.  onLevel is called with the array it was removed from so the
 * caller can repack positions.
 */ function detachQuestion(questions, questionId, onLevel) {
    const idx = questions.findIndex((q)=>q.questionId === questionId);
    if (idx !== -1) {
        const [removed] = questions.splice(idx, 1);
        onLevel(questions);
        return removed;
    }
    for (const q of questions){
        var _q_options;
        for (const opt of (_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []){
            if (opt.branches) {
                const hit = detachQuestion(opt.branches, questionId, onLevel);
                if (hit) return hit;
            }
        }
        if (q.rows) {
            const hit = detachQuestion(q.rows, questionId, onLevel);
            if (hit) return hit;
        }
    }
    return null;
}
/**
 * Patch every occurrence of tempId in the tree with realId.
 * Covers: questionId, optionId, branchingFrom (optionId ref), parentId (questionId ref).
 */ function patchTempIdInTree(questions, tempId, realId) {
    for (const q of questions){
        if (q.questionId === tempId) q.questionId = realId;
        if (q.branchingFrom === tempId) q.branchingFrom = realId;
        if (q.parentId === tempId) q.parentId = realId;
        if (q.options) {
            for (const opt of q.options){
                if (opt.optionId === tempId) opt.optionId = realId;
                if (opt.branches) patchTempIdInTree(opt.branches, tempId, realId);
            }
        }
        if (q.rows) patchTempIdInTree(q.rows, tempId, realId);
    }
}
const initialState = {
    draftTemplate: null,
    isDirty: false,
    dirtyQuestionIds: [],
    deletedQuestionIds: [],
    deletedOptionIds: [],
    dirtyMetadataKeys: [],
    dirtyThemeKeys: [],
    lastIdMappings: null,
    pendingSave: null,
    propagateAutomation: false
};
// ─── Change-tracking helpers ──────────────────────────────────────────────────
/** Mark a single question as needing a write, and the draft as dirty. */ function markQuestionDirty(state, questionId) {
    if (!state.dirtyQuestionIds.includes(questionId)) {
        state.dirtyQuestionIds.push(questionId);
    }
    state.isDirty = true;
}
/**
 * After a level has been repacked, mark dirty only the questions whose position
 * actually moved.  Appending to the end, or removing the last question, shifts
 * nobody — so untouched siblings stay out of the request entirely.  A question
 * absent from `before` is new, and therefore always counts as moved.
 */ function markMovedQuestionsDirty(state, before, after) {
    const previousPositions = new Map(before.map((q)=>[
            q.questionId,
            q.position
        ]));
    after.forEach((q)=>{
        if (previousPositions.get(q.questionId) !== q.position) {
            markQuestionDirty(state, q.questionId);
        }
    });
}
/**
 * Renumber a level 1..n in place, marking dirty only the questions whose position
 * actually changed.  The in-place counterpart to markMovedQuestionsDirty, for the
 * paths that splice the live array rather than rebuilding it.
 */ function repackLevelInPlace(state, level) {
    level.forEach((q, idx)=>{
        const nextPosition = idx + 1;
        if (q.position !== nextPosition) {
            q.position = nextPosition;
            markQuestionDirty(state, q.questionId);
        }
    });
}
/**
 * Drop every question in a subtree from dirtyQuestionIds — there is no point
 * writing rows that are about to be (or have already been) deleted.
 */ function pruneDirtySubtree(state, roots) {
    const allIds = collectQuestionIds(roots, []);
    state.dirtyQuestionIds = state.dirtyQuestionIds.filter((id)=>!allIds.includes(id));
}
/**
 * Record a subtree as explicitly deleted.  Only the subtree roots are sent —
 * the server cascade-deletes their branches and rows, so listing descendants
 * would double-delete.  Temp-ID questions were never persisted, so they are
 * dropped silently.
 */ function recordDeletedSubtree(state, roots) {
    pruneDirtySubtree(state, roots);
    roots.forEach((root)=>{
        if (!isClientId(root.questionId)) {
            state.deletedQuestionIds.push(root.questionId);
        }
    });
}
/** Record a template-level field as needing a write. */ function markMetadataDirty(state, key) {
    if (!state.dirtyMetadataKeys.includes(key)) {
        state.dirtyMetadataKeys.push(key);
    }
}
/** Record the formTheme fields a write touched, so the save sends only those. */ function markThemeKeysDirty(state, theme) {
    markMetadataDirty(state, 'formTheme');
    Object.keys(theme).forEach((key)=>{
        if (!state.dirtyThemeKeys.includes(key)) {
            state.dirtyThemeKeys.push(key);
        }
    });
}
/** Reset all change tracking — the draft now matches the server. */ function resetTracking(state) {
    state.isDirty = false;
    state.dirtyQuestionIds = [];
    state.deletedQuestionIds = [];
    state.deletedOptionIds = [];
    state.dirtyMetadataKeys = [];
    state.dirtyThemeKeys = [];
    state.pendingSave = null;
    state.lastIdMappings = null;
    state.propagateAutomation = false;
}
/**
 * Recompute isDirty from the tracking sets.  Used after a save subtracts what it
 * persisted, so the Save button re-enables when edits arrived mid-flight.
 */ function recomputeIsDirty(state) {
    state.isDirty = state.dirtyQuestionIds.length > 0 || state.deletedQuestionIds.length > 0 || state.deletedOptionIds.length > 0 || state.dirtyMetadataKeys.length > 0;
}
// ─── Slice ────────────────────────────────────────────────────────────────────
export const formBuilderDraftSlice = createSlice({
    name: '@@FORM_BUILDER_DRAFT',
    initialState,
    reducers: {
        // ------------------------------------------------------------------
        // Lifecycle
        // ------------------------------------------------------------------
        /**
     * Initialise (or re-initialise) the draft from the server-returned
     * tree-structured FormTemplate.  Resets isDirty to false.
     */ initDraft: (state, { payload })=>{
            state.draftTemplate = payload;
            resetTracking(state);
        },
        /**
     * Drop the in-memory draft. Used when the user confirms leave-without-saving
     * so a later remount of the same template does not restore discarded edits.
     */ clearDraft: ()=>initialState,
        // ------------------------------------------------------------------
        // Template metadata (name, description, theme, settings, automation)
        // ------------------------------------------------------------------
        updateMetadata: (state, { payload })=>{
            if (!state.draftTemplate) return;
            Object.assign(state.draftTemplate, payload);
            Object.keys(payload).forEach((key)=>{
                markMetadataDirty(state, key);
            });
            // A caller that writes the whole block owns every key in it.
            if (payload.formTheme) {
                markThemeKeysDirty(state, payload.formTheme);
            }
            state.isDirty = true;
        },
        /**
     * Merge a partial formTheme into the draft.
     *
     * The theme panes used to read their `formTheme` prop, spread it and send
     * the whole block back through updateMetadata. That is a read-modify-write
     * over a render snapshot, and the image upload completes a network round
     * trip later — long enough for the brightness slider or a different header
     * selection to have landed in between, which the resolving upload then
     * overwrote. Merging into the live draft here keeps each control writing
     * only the keys it owns.
     */ updateFormTheme: (state, { payload })=>{
            if (!state.draftTemplate) return;
            state.draftTemplate.formTheme = {
                ...state.draftTemplate.formTheme,
                ...payload
            };
            markThemeKeysDirty(state, payload);
            state.isDirty = true;
        },
        // ------------------------------------------------------------------
        // Questions
        // ------------------------------------------------------------------
        /**
     * Add a new question to the draft.
     * The caller must assign a temp ID before dispatching:
     *   const id = nextClientId();
     *   dispatch(addQuestion({ question: { questionId: id, ... }, branchingFrom, insertAtPosition }));
     */ addQuestion: (state, { payload })=>{
            if (!state.draftTemplate) return;
            const { question, insertAtPosition, branchingFrom } = payload;
            if (branchingFrom !== undefined) {
                applyToOption(state.draftTemplate.formQuestions, branchingFrom, (opt)=>{
                    var _opt_branches;
                    const before = (_opt_branches = opt.branches) !== null && _opt_branches !== void 0 ? _opt_branches : [];
                    const after = addQuestionAtPosition(before, question, insertAtPosition);
                    // Only siblings the insert actually pushed down need writing.
                    markMovedQuestionsDirty(state, before, after);
                    opt.branches = after;
                });
            } else {
                const before = state.draftTemplate.formQuestions;
                const after = addQuestionAtPosition(before, question, insertAtPosition);
                markMovedQuestionsDirty(state, before, after);
                state.draftTemplate.formQuestions = after;
            }
            markQuestionDirty(state, question.questionId);
        },
        /** Update fields on an existing question anywhere in the tree. */ updateQuestion: (state, { payload })=>{
            if (!state.draftTemplate) return;
            // Only mark dirty when the question is actually in the tree. A properties
            // panel holding a pre-save snapshot dispatches temp IDs the draft no
            // longer has; marking those would queue a write for a question that was
            // never touched (and, for options, re-send its whole option list).
            const found = applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                Object.assign(q, payload.changes);
            });
            if (found) markQuestionDirty(state, payload.questionId);
        },
        /**
     * Delete a question anywhere in the tree.
     * Cascade: because branches are stored nested under option.branches in the
     * tree structure, removing a question automatically removes all its children.
     */ deleteQuestion: (state, { payload })=>{
            if (!state.draftTemplate) return;
            // Record the deletion before mutating, while the subtree is still reachable.
            const doomed = findQuestion(state.draftTemplate.formQuestions, payload.questionId);
            if (!doomed) return;
            recordDeletedSubtree(state, [
                doomed
            ]);
            state.draftTemplate.formQuestions = removeQuestionFromTree(state.draftTemplate.formQuestions, payload.questionId, (before, after)=>markMovedQuestionsDirty(state, before, after));
            state.isDirty = true;
        },
        /**
     * Move a question (with its whole subtree) to a new branch level and position.
     * Used by drag-to-reorder, which can both re-order within a level and re-parent
     * onto a different option.  branchingFrom null/undefined means the root level.
     */ moveQuestion: (state, { payload })=>{
            if (!state.draftTemplate) return;
            const { questionId, branchingFrom, position } = payload;
            const roots = state.draftTemplate.formQuestions;
            const moving = findQuestion(roots, questionId);
            if (!moving) return;
            // Refuse to drop a question inside its own subtree — that would detach the
            // branch from the tree entirely and lose it.
            if (branchingFrom !== null && branchingFrom !== undefined && collectOptionIds([
                moving
            ], []).includes(branchingFrom)) {
                return;
            }
            // Resolve the destination before detaching, so a bad target cannot strand
            // the question outside the tree.
            const targetLevel = getBranchLevel(roots, branchingFrom);
            if (!targetLevel) return;
            const moved = detachQuestion(roots, questionId, (sourceLevel)=>repackLevelInPlace(state, sourceLevel));
            if (!moved) return;
            moved.branchingFrom = branchingFrom !== null && branchingFrom !== void 0 ? branchingFrom : undefined;
            const insertAt = Math.max(0, Math.min(position - 1, targetLevel.length));
            targetLevel.splice(insertAt, 0, moved);
            repackLevelInPlace(state, targetLevel);
            // The move itself always needs writing, even if its position number happens
            // to be unchanged — its parent may have changed.
            markQuestionDirty(state, questionId);
        },
        // ------------------------------------------------------------------
        // Matrix rows
        // ------------------------------------------------------------------
        /**
     * Append a row to a matrix question.  The caller assigns the temp ID:
     *   dispatch(addRow({ questionId, row: { questionId: nextClientId(), ... } }));
     */ addRow: (state, { payload })=>{
            if (!state.draftTemplate) return;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_rows;
                const rows = (_q_rows = q.rows) !== null && _q_rows !== void 0 ? _q_rows : [];
                q.rows = [
                    ...rows,
                    {
                        ...payload.row,
                        parentId: payload.questionId,
                        position: rows.length + 1
                    }
                ];
            });
            // The parent carries the row set; the row itself is new.
            markQuestionDirty(state, payload.questionId);
            markQuestionDirty(state, payload.row.questionId);
        },
        /**
     * Re-order a matrix question's rows after a drag.  Only the ordering is taken
     * from the payload — the rows themselves stay the draft's own (see
     * reorderById) — and only rows that actually moved are marked for writing.
     */ reorderRows: (state, { payload })=>{
            if (!state.draftTemplate) return;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_rows;
                const before = (_q_rows = q.rows) !== null && _q_rows !== void 0 ? _q_rows : [];
                const after = repackPositions(reorderById(before, payload.rows, (row)=>row.questionId));
                markMovedQuestionsDirty(state, before, after);
                q.rows = after;
            });
            state.isDirty = true;
        },
        // ------------------------------------------------------------------
        // Options
        // ------------------------------------------------------------------
        /**
     * Add a new option to a question.
     * The caller must assign a temp ID before dispatching:
     *   dispatch(addOption({ questionId, option: { optionId: nextClientId(), ... } }));
     */ addOption: (state, { payload })=>{
            if (!state.draftTemplate) return;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_options;
                const existing = (_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : [];
                q.options = [
                    ...existing,
                    {
                        ...payload.option,
                        position: existing.length + 1
                    }
                ];
            });
            markQuestionDirty(state, payload.questionId);
        },
        /** Update fields on an existing option. */ updateOption: (state, { payload })=>{
            if (!state.draftTemplate) return;
            let updated = false;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_options;
                const opt = ((_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []).find((o)=>o.optionId === payload.optionId);
                if (opt) {
                    Object.assign(opt, payload.changes);
                    updated = true;
                }
            });
            if (updated) markQuestionDirty(state, payload.questionId);
        },
        /**
     * Delete an option from a question.
     * Cascade: any questions branching off the deleted option are also removed
     * because they are stored in option.branches.
     */ deleteOption: (state, { payload })=>{
            if (!state.draftTemplate) return;
            let removed = false;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_options, _doomed_branches, _q_options1;
                const doomed = ((_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : []).find((o)=>o.optionId === payload.optionId);
                if (!doomed) return;
                // deleteOption cascades into branches server-side, so the branch
                // questions must NOT be listed again — only pruned from the dirty set.
                pruneDirtySubtree(state, (_doomed_branches = doomed.branches) !== null && _doomed_branches !== void 0 ? _doomed_branches : []);
                if (!isClientId(doomed.optionId)) {
                    state.deletedOptionIds.push(doomed.optionId);
                }
                q.options = ((_q_options1 = q.options) !== null && _q_options1 !== void 0 ? _q_options1 : []).filter((o)=>o.optionId !== payload.optionId).map((o, idx)=>({
                        ...o,
                        position: idx + 1
                    }));
                removed = true;
            });
            // An option the draft does not have (a stale panel snapshot naming a
            // pre-save temp ID) must not dirty the question: the save would then
            // re-send its full option list on the user's behalf.
            if (removed) markQuestionDirty(state, payload.questionId);
        },
        /**
     * Re-order a question's options after a drag.  Only the ordering is taken
     * from the payload; the option objects (and their branches) stay the draft's
     * own, and positions are renumbered here rather than by the caller.
     */ reorderOptions: (state, { payload })=>{
            if (!state.draftTemplate) return;
            applyToQuestion(state.draftTemplate.formQuestions, payload.questionId, (q)=>{
                var _q_options;
                q.options = reorderById((_q_options = q.options) !== null && _q_options !== void 0 ? _q_options : [], payload.options, (opt)=>opt.optionId).map((opt, idx)=>({
                        ...opt,
                        position: idx + 1
                    }));
            });
            markQuestionDirty(state, payload.questionId);
        },
        // ------------------------------------------------------------------
        // Branching
        // ------------------------------------------------------------------
        /** Clear all branching questions off a specific option (delete-branch action). */ deleteBranch: (state, { payload })=>{
            if (!state.draftTemplate) return;
            applyToOption(state.draftTemplate.formQuestions, payload.optionId, (opt)=>{
                var _opt_branches;
                // The option itself survives, so nothing cascades server-side — every
                // branch root must be listed explicitly.
                recordDeletedSubtree(state, (_opt_branches = opt.branches) !== null && _opt_branches !== void 0 ? _opt_branches : []);
                opt.branches = [];
            });
            state.isDirty = true;
        },
        // ------------------------------------------------------------------
        // Post-save reconciliation
        // ------------------------------------------------------------------
        /**
     * Move the current change set aside just before a save request goes out.
     *
     * The live tracking sets are emptied, so any edit made while the request is
     * in flight repopulates them from scratch and re-enables Save.  That is what
     * keeps mid-flight edits from being mistaken for "already saved" — even when
     * they touch a question the in-flight request also carried.
     */ beginSave: (state)=>{
            state.pendingSave = {
                dirtyQuestionIds: [
                    ...state.dirtyQuestionIds
                ],
                deletedQuestionIds: [
                    ...state.deletedQuestionIds
                ],
                deletedOptionIds: [
                    ...state.deletedOptionIds
                ],
                dirtyMetadataKeys: [
                    ...state.dirtyMetadataKeys
                ],
                dirtyThemeKeys: [
                    ...state.dirtyThemeKeys
                ],
                propagateAutomation: state.propagateAutomation
            };
            state.dirtyQuestionIds = [];
            state.deletedQuestionIds = [];
            state.deletedOptionIds = [];
            state.dirtyMetadataKeys = [];
            state.dirtyThemeKeys = [];
            // Consumed by the request now in flight. Not carried forward: a later save
            // must not fan out to live customer forms again without a fresh answer.
            state.propagateAutomation = false;
            recomputeIsDirty(state);
        },
        /**
     * Put an in-flight change set back after a failed save, merged with anything
     * edited since, so nothing is silently dropped on error.
     */ rollbackSave: (state)=>{
            const pending = state.pendingSave;
            if (!pending) return;
            pending.dirtyQuestionIds.forEach((id)=>{
                if (!state.dirtyQuestionIds.includes(id)) {
                    state.dirtyQuestionIds.push(id);
                }
            });
            pending.deletedQuestionIds.forEach((id)=>{
                if (!state.deletedQuestionIds.includes(id)) {
                    state.deletedQuestionIds.push(id);
                }
            });
            pending.deletedOptionIds.forEach((id)=>{
                if (!state.deletedOptionIds.includes(id)) {
                    state.deletedOptionIds.push(id);
                }
            });
            pending.dirtyMetadataKeys.forEach((key)=>{
                if (!state.dirtyMetadataKeys.includes(key)) {
                    state.dirtyMetadataKeys.push(key);
                }
            });
            pending.dirtyThemeKeys.forEach((key)=>{
                if (!state.dirtyThemeKeys.includes(key)) {
                    state.dirtyThemeKeys.push(key);
                }
            });
            // The failed save never propagated, so the user's answer still stands. A
            // newer answer given since takes precedence (last answer wins).
            state.propagateAutomation = state.propagateAutomation || pending.propagateAutomation;
            state.pendingSave = null;
            recomputeIsDirty(state);
        },
        /**
     * Record the user's answer to "apply this to forms already sent to customers?".
     * Dispatched alongside the automation edit itself; the latest answer before Save
     * is the one that travels.
     */ setPropagateAutomation: (state, { payload })=>{
            state.propagateAutomation = payload;
        },
        /**
     * Apply the server's idMappings after a successful bulk-save.
     * Keys use the format "tmp_q_N" (questions) or "tmp_o_N" (options).
     *
     * Discards the in-flight change set (it is now persisted) but keeps whatever
     * accumulated since, rewriting any temp IDs in it to the real IDs the server
     * just assigned so a mid-flight edit to a brand-new question still saves.
     */ applyIdMappings: (state, { payload })=>{
            if (!state.draftTemplate) return;
            Object.entries(payload).forEach(([key, realId])=>{
                const m = key.match(/^tmp_([qo])_(\d+)$/);
                if (!m) return;
                const tempId = -parseInt(m[2], 10);
                patchTempIdInTree(state.draftTemplate.formQuestions, tempId, realId);
            });
            const toRealId = (id)=>{
                if (!isClientId(id)) return id;
                const realId = payload[clientIdKey('q', id)];
                return realId === undefined ? id : realId;
            };
            state.dirtyQuestionIds = Array.from(new Set(state.dirtyQuestionIds.map(toRealId)));
            state.pendingSave = null;
            state.lastIdMappings = payload;
            recomputeIsDirty(state);
        }
    }
});
export const formBuilderDraftActions = formBuilderDraftSlice.actions;
export const formBuilderDraftReducer = formBuilderDraftSlice.reducer;
